import { useParams, Navigate, Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCompanies, useSummary, useQuote, useConceptCatalog, usePeers } from "../../hooks/useQueries";
import { useQueryClient } from "@tanstack/react-query";
import { VALID_PANELS } from "../../utils/constants";
import type { PanelName, AccountingStandard } from "../../utils/constants";
import Layout from "../../components/Layout";
import { CombavaSpinner } from "../../components/CombavaSpinner";
import { useState, useCallback, useEffect, Suspense, lazy } from "react";
import { ingestTickers, reingestTicker } from "../../api/client";

const OverviewPanel = lazy(() => import("./OverviewPanel"));
const StatementsPanel = lazy(() => import("./StatementsPanel"));
const ComparePanel = lazy(() => import("./ComparePanel"));
const FilingsPanel = lazy(() => import("./FilingsPanel"));
const OwnershipPanel = lazy(() => import("./OwnershipPanel"));

export default function TickerPage() {
  const { exchange: rawExchange, ticker: rawTicker, panel: rawPanel } = useParams();
  const exchange = rawExchange?.toUpperCase() ?? "US";
  const ticker = rawTicker?.toUpperCase() ?? "";
  const panel = (
    rawPanel && VALID_PANELS.includes(rawPanel as PanelName)
      ? rawPanel
      : "overview"
  ) as PanelName;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { data: companiesData, isLoading: companiesLoading } = useCompanies();
  const { data: summary, isLoading: summaryLoading } = useSummary(exchange, ticker);
  const { data: quote } = useQuote(ticker);

  // Concept catalog — includes staleness metadata
  const { data: catalog } = useConceptCatalog(exchange, ticker);

  // Peer data
  const { data: peers, isLoading: peersLoading } = usePeers(ticker);
  const peerCount = peers?.length || 0;

  // Navigate to Financials tab with a pre-selected metric
  const handleExpandChart = useCallback(
    (metric: string) => {
      navigate(`/stocks/${exchange}/${ticker.toLowerCase()}/financials?metric=${metric}`);
    },
    [navigate, exchange, ticker],
  );

  // Ingestion state
  const [ingesting, setIngesting] = useState(false);
  const [ingestError, setIngestError] = useState<string | null>(null);
  const [ingestDone, setIngestDone] = useState(false);

  // Re-ingest state
  const [reingesting, setReingesting] = useState(false);
  const [reingestDismissed, setReingestDismissed] = useState(false);

  // Check if this company has ingested data
  const companies = companiesData?.companies ?? [];
  const company = companies.find((c) => c.ticker?.toUpperCase() === ticker?.toUpperCase());
  const hasData = companiesLoading || !!company;

  // Auto-redirect to the correct exchange if the URL exchange is "US" or incorrect but we have the resolved company's exchange
  useEffect(() => {
    if (company && company.exchange && company.exchange.toUpperCase() !== exchange.toUpperCase()) {
      navigate(`/stocks/${company.exchange}/${ticker.toLowerCase()}/${panel}`, { replace: true });
    }
  }, [company, exchange, ticker, panel, navigate]);

  // Stale data detection
  const isStale = catalog?.is_stale && !reingestDismissed;

  // If raw panel was missing, redirect to include it
  if (!rawPanel) {
    return <Navigate to={`/stocks/${exchange}/${ticker.toLowerCase()}/overview`} replace />;
  }

  // Redirect away from compare panel if it shouldn't be accessible
  useEffect(() => {
    if (!peersLoading && peerCount < 3 && panel === "compare") {
      navigate(`/stocks/${exchange}/${ticker.toLowerCase()}/overview`, { replace: true });
    }
  }, [peersLoading, peerCount, panel, navigate, exchange, ticker]);

  const metrics = summary?.metrics ?? {};
  const currency = summary?.company?.currency ?? company?.currency ?? "USD";
  const rawStandard = summary?.company?.accounting_standard || "US_GAAP";
  const accountingStandard: AccountingStandard = rawStandard === "IFRS" ? "ifrs-full" : "us-gaap";

  // Handle re-ingest for stale data
  const handleReingest = useCallback(async () => {
    setReingesting(true);
    try {
      await reingestTicker(ticker);
      // Refresh everything
      await queryClient.invalidateQueries({ queryKey: ["companies"] });
      await queryClient.invalidateQueries({ queryKey: ["summary", exchange, ticker] });
      await queryClient.invalidateQueries({ queryKey: ["conceptCatalog", exchange, ticker] });
      await queryClient.invalidateQueries({ queryKey: ["trend", exchange, ticker] });
      await queryClient.invalidateQueries({ queryKey: ["filings", ticker] });
      await queryClient.invalidateQueries({ queryKey: ["insiders", ticker] });
      await queryClient.invalidateQueries({ queryKey: ["insiderTrades", ticker] });
      await queryClient.invalidateQueries({ queryKey: ["institutionalHolders", ticker] });
      await queryClient.invalidateQueries({ queryKey: ["compare"] });
      setReingestDismissed(true);
    } catch {
      // silently fail — user can retry
    } finally {
      setReingesting(false);
    }
  }, [ticker, queryClient]);

  // Handle "Fetch Data" click
  const handleFetchData = useCallback(async () => {
    setIngesting(true);
    setIngestError(null);

    try {
      const result = await ingestTickers({
        tickers: [ticker],
        form_types: ["10-Q", "10-K", "20-F", "40-F", "6-K"],
      });

      const item = result.results[0];
      if (item && !item.success) {
        setIngestError(item.errors[0] || "Ingestion failed");
      } else {
        setIngestDone(true);
        // Refresh the companies list so the app knows we have data now
        await queryClient.invalidateQueries({ queryKey: ["companies"] });
        await queryClient.invalidateQueries({ queryKey: ["summary", exchange, ticker] });
      }
    } catch (err: any) {
      setIngestError(err.message || "Failed to fetch data");
    } finally {
      setIngesting(false);
    }
  }, [ticker, queryClient]);

  // ── No data state ──────────────────────────────────────────────────
  if (!hasData && !ingestDone) {
    return (
      <Layout ticker={ticker}>
        <NoDataView
          ticker={ticker}
          ingesting={ingesting}
          ingestError={ingestError}
          onFetchData={handleFetchData}
        />
      </Layout>
    );
  }

  return (
    <Layout activePanel={panel} ticker={ticker}>
      <main className="flex-1 flex flex-col w-full max-w-[1600px] mx-auto px-4 md:px-6 py-6 gap-6">
        {/* ═══ COMPANY HERO ═══ */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pb-4 border-b border-card-border">
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline gap-3">
              <h1 className="text-white text-4xl font-black tracking-tighter font-mono">
                {ticker}
              </h1>
              {company ? (
                <span className="text-xl text-white font-medium">
                  {company.name}
                </span>
              ) : (
                <div className="w-48 h-7 bg-white/10 rounded animate-pulse" />
              )}
            </div>
            <div className="flex items-center gap-3 text-sm min-h-[20px]">
              {company ? (
                <>
                  {company.exchange && (
                    <span className="px-2 py-0.5 rounded bg-card-border text-text-secondary font-medium font-mono text-xs">
                      {company.exchange}
                    </span>
                  )}
                  {company.is_spac && (
                    <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium font-mono text-xs border border-amber-500/30">
                      SPAC / Shell Company
                    </span>
                  )}
                  {company.sic_code && !company.is_spac && (
                    <span className="text-text-secondary" title={`SIC ${company.sic_code}`}>
                      {company.sic_description || `SIC ${company.sic_code}`}
                    </span>
                  )}
                  <span className="text-text-secondary">
                    {company.filings_count} filings · {company.facts_count} facts
                  </span>
                </>
              ) : (
                <div className="w-64 h-5 bg-white/5 rounded animate-pulse" />
              )}
            </div>
          </div>

          {/* Sync button — far right */}
          <button
            onClick={handleReingest}
            disabled={reingesting}
            title={reingesting ? "Syncing data…" : "Re-sync SEC filings & XBRL data"}
            className={`group flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all cursor-pointer shrink-0 ${
              reingesting
                ? "border-primary/30 bg-primary/10 text-primary/60 cursor-wait"
                : "border-card-border bg-card-dark text-text-secondary hover:text-white hover:border-primary/50 hover:bg-primary/10"
            }`}
          >
            <svg
              className={`size-4 ${reingesting ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            {reingesting ? "Syncing…" : "Sync"}
          </button>
        </div>

        {/* ═══ STALE DATA BANNER ═══ */}
        {isStale && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/30 animate-fade-in">
            <svg className="size-5 text-amber-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-200 font-medium">
                Expanded financial data available
              </p>
              <p className="text-xs text-amber-200/60">
                This company was ingested with an older data scope. Re-ingest to capture all {catalog?.current_version === 2 ? "300–1,000+" : ""} XBRL concepts including non-GAAP metrics and company-specific items.
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleReingest}
                disabled={reingesting}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  reingesting
                    ? "bg-amber-500/20 text-amber-300/60 cursor-wait"
                    : "bg-amber-500/20 text-amber-300 hover:bg-amber-500/30"
                }`}
              >
                {reingesting ? "Re-ingesting…" : "Update Data"}
              </button>
              <button
                onClick={() => setReingestDismissed(true)}
                className="text-amber-300/40 hover:text-amber-300 transition-colors cursor-pointer"
              >
                <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* ═══ SUB-TAB BAR ═══ */}
        <div className="flex overflow-x-auto no-scrollbar gap-8 border-b border-card-border min-h-[34px]">
          {(
            [
              { key: "overview", label: "Overview" },
              { key: "financials", label: "Financials" },
              ...(peersLoading || peerCount >= 3 ? [{ key: "compare", label: "Peers" }] : []),
              { key: "filings", label: "Filings" },
              { key: "ownership", label: "Insiders" },
            ] as const
          ).map((tab) => (
            <Link
              key={tab.key}
              to={`/stocks/${exchange}/${ticker.toLowerCase()}/${tab.key}`}
              className={`pb-3 border-b-2 text-sm cursor-pointer whitespace-nowrap no-underline ${
                panel === tab.key
                  ? "border-primary text-white font-bold"
                  : "border-transparent text-text-secondary hover:text-white font-medium"
              }`}
            >
              {tab.label}
            </Link>
          ))}
        </div>

        {/* ═══ ACTIVE PANEL ═══ */}
        <div className="fade-in">
          <Suspense fallback={<div className="flex items-center justify-center h-64"><CombavaSpinner className="size-8" /></div>}>
            {panel === "overview" && (
              <OverviewPanel
                exchange={exchange}
                ticker={ticker}
                metrics={metrics}
                loading={summaryLoading}
                onExpandChart={handleExpandChart}
                currency={currency}
                description={summary?.company?.description}
                quote={quote}
              />
            )}
            {panel === "financials" && (
              <StatementsPanel exchange={exchange} ticker={ticker} currency={currency} initialMetric={searchParams.get("metric") ?? undefined} accountingStandard={accountingStandard} />
            )}
            {panel === "compare" && <ComparePanel exchange={exchange} ticker={ticker} accountingStandard={accountingStandard} />}
            {panel === "filings" && <FilingsPanel />}
            {panel === "ownership" && <OwnershipPanel />}
          </Suspense>
        </div>
      </main>

    </Layout>
  );
}


/* ═══════════════════════════════════════════════════════════════════════
   No Data View — shown when user navigates to a ticker without data
   ═══════════════════════════════════════════════════════════════════════ */

function NoDataView({
  ticker,
  ingesting,
  ingestError,
  onFetchData,
}: {
  ticker: string;
  ingesting: boolean;
  ingestError: string | null;
  onFetchData: () => void;
}) {
  const { data: quote } = useQuote(ticker);

  return (
    <main className="flex-1 flex flex-col items-center justify-center px-4 py-16 gap-8">
      {/* Ticker header */}
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-white text-5xl font-black tracking-tighter font-mono">
          {ticker}
        </h1>
        {quote?.longName && (
          <p className="text-text-secondary text-lg">{quote.longName}</p>
        )}
        {quote?.sector && (
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <span>{quote.sector}</span>
            {quote.industry && (
              <>
                <span className="text-card-border">·</span>
                <span>{quote.industry}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Live quote card (if available) */}
      {quote?.price != null && (
        <div className="bg-card-dark rounded-xl border border-card-border p-6 w-full max-w-md shadow-sm shadow-black/50">
          <div className="flex items-baseline justify-between mb-4">
            <div className="flex items-baseline gap-2">
              <span className="text-white text-3xl font-bold font-mono">
                ${quote.price.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <span className="text-xs text-text-secondary font-mono">
                {quote.currency}
              </span>
            </div>
            {quote.previousClose != null && (
              <span
                className={`text-sm font-bold ${
                  quote.price >= quote.previousClose
                    ? "text-accent-green"
                    : "text-accent-red"
                }`}
              >
                {quote.price >= quote.previousClose ? "+" : ""}
                {((quote.price - quote.previousClose) / quote.previousClose * 100).toFixed(2)}%
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {[
              { label: "Mkt Cap", value: quote.marketCap ? `$${(quote.marketCap / 1e9).toFixed(1)}B` : null },
              { label: "P/E", value: quote.trailingPE?.toFixed(1) },
              { label: "52w High", value: quote.fiftyTwoWeekHigh ? `$${quote.fiftyTwoWeekHigh.toFixed(2)}` : null },
              { label: "52w Low", value: quote.fiftyTwoWeekLow ? `$${quote.fiftyTwoWeekLow.toFixed(2)}` : null },
              { label: "Volume", value: quote.volume ? quote.volume.toLocaleString() : null },
              { label: "Beta", value: quote.beta?.toFixed(2) },
            ]
              .filter((r) => r.value)
              .map((r) => (
                <div key={r.label} className="flex justify-between">
                  <span className="text-text-secondary">{r.label}</span>
                  <span className="text-white font-mono">{r.value}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* CTA */}
      <div className="flex flex-col items-center gap-4 max-w-md text-center">
        <p className="text-text-secondary text-sm">
          No SEC filing data has been loaded for this ticker yet.
          Fetch financial data to see revenue, earnings, balance sheet, and more.
        </p>

        <button
          onClick={onFetchData}
          disabled={ingesting}
          className={`relative px-8 py-3 rounded-xl font-bold text-sm transition-all cursor-pointer ${
            ingesting
              ? "bg-primary/30 text-primary/70 cursor-wait"
              : "bg-primary text-white hover:bg-primary/90 shadow-lg shadow-primary/20"
          }`}
        >
          {ingesting ? (
            <span className="flex items-center gap-3">
              <LoadingSpinner />
              Fetching SEC filings...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Fetch Financial Data
            </span>
          )}
        </button>

        {ingesting && (
          <div className="flex flex-col items-center gap-2 animate-fade-in">
            <p className="text-xs text-text-secondary">
              Downloading filings from SEC EDGAR and parsing XBRL data...
            </p>
            <div className="w-48 h-1.5 rounded-full bg-card-border overflow-hidden">
              <div className="h-full bg-primary rounded-full animate-progress" />
            </div>
          </div>
        )}

        {ingestError && (
          <div className="mt-2 px-4 py-2 rounded-lg bg-accent-red/10 border border-accent-red/30 text-accent-red text-sm">
            {ingestError}
          </div>
        )}
      </div>
    </main>
  );
}


function LoadingSpinner() {
  return <CombavaSpinner className="size-5" />;
}
